import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, X } from 'lucide-react';
import { useTrialStatus } from '../hooks/useFeatureAccess';
import { openUsageModal } from '../store/ui$';
import { trackEvent } from '../lib/analytics';
import './TrialBanner.css';
import { ICON_SIZE } from '../design-system';

/**
 * TrialBanner — appears when trial has <= 9 days remaining.
 * Fixed at top of page. Dismissible (reappears on next app open).
 * Threshold of 9 closes the gap with trialChip (visible at >=10 days).
 */
const TrialBanner = () => {
  const { t } = useTranslation('common');
  const { isInTrial, trialDaysLeft } = useTrialStatus();
  const [dismissed, setDismissed] = useState(false);

  // Show when <=9 days remain — closes gap between trialChip (visible >=10) and this banner.
  const shouldShow = isInTrial && trialDaysLeft !== null && trialDaysLeft <= 9 && !dismissed;

  useEffect(() => {
    if (shouldShow) {
      trackEvent('trial_banner_shown', { trialDaysLeft });
    }
  }, [shouldShow, trialDaysLeft]);

  if (!shouldShow) {
    return null;
  }

  const handleUpgrade = () => {
    trackEvent('trial_banner_upgrade_clicked', { trialDaysLeft });
    openUsageModal('plan', 'trial_ending');
  };

  const daysText = trialDaysLeft <= 0
    ? t('trial.banner.lastDay', { defaultValue: 'Last day' })
    : trialDaysLeft === 1
      ? t('trial.banner.oneDay', { defaultValue: '1 day left' })
      : t('trial.banner.daysLeft', { defaultValue: '{{days}} days left', days: trialDaysLeft });

  return (
    <div className="trial-banner">
      <div className="trial-banner-content">
        <Crown size={ICON_SIZE.xs} className="trial-banner-icon" />
        <span className="trial-banner-text">
          {t('trial.banner.message', {
            defaultValue: 'Your Pro trial ends in {{days}} — upgrade to keep all features',
            days: daysText,
          })}
        </span>
        <button className="trial-banner-cta" onClick={handleUpgrade}>
          {t('trial.banner.upgrade', { defaultValue: 'Upgrade' })}
        </button>
      </div>
      <button className="trial-banner-close" onClick={() => setDismissed(true)}>
        <X size={ICON_SIZE.xs} />
      </button>
    </div>
  );
};

export default TrialBanner;
